import { Flex } from '@chakra-ui/react';
import {
  FormFieldDoubleAnonymousPeerReview,
  FormFieldGeneratedDataset,
  FormFieldPreferenceImmediatePublication,
  FormFieldTransparentPeerReview,
  FormFieldUtilizedDataset
} from '@publishing-engine/domain/types/article';
import React, { useState } from 'react';
import { FieldHeader } from '../common/FieldHeader';
import BooleanUpdateModal from './BooleanUpdateModal';
import { BooleanDiffModal } from './BooleanDiffModal';
import { BooleanDisplay } from './BooleanDisplay';
import { BooleanEmptyState } from './BooleanEmptyState';
import { IAvailableWorkflowActions } from '@publishing-engine/domain/models';
import { EntryPoint } from 'src/utils/types';
import { useSubmissionFields } from 'src/hooks/useSubmissionFields';

export type FormFieldBoolean =
  | FormFieldPreferenceImmediatePublication
  | FormFieldTransparentPeerReview
  | FormFieldDoubleAnonymousPeerReview
  | FormFieldGeneratedDataset
  | FormFieldUtilizedDataset;

type IProps = {
  formFieldDefinition: FormFieldBoolean;
  value: boolean;
  previousValue?: boolean | null;
  availableActions: IAvailableWorkflowActions;
  onChange?: (value: boolean) => void;
  entryPoint: EntryPoint;
  isUpdateRequired?:boolean;
};

export const GenericBooleanField: React.FC<IProps> = ({
  formFieldDefinition,
  value,
  previousValue,
  availableActions,
  onChange,
  entryPoint,
  isUpdateRequired
}) => {
  const { checkFormFieldValueIsUpdated } = useSubmissionFields(entryPoint);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [showDiffModal, setShowDiffModal] = useState<boolean>(false);
  const openDiffModal = () => setShowDiffModal(true);
  const closeDiffModal = () => setShowDiffModal(false);
  const isEditable = !!onChange;

  const editCallback = isEditable
    ? () => {
        setIsEditing(true);
      }
    : undefined;

  const isUpdated = checkFormFieldValueIsUpdated(formFieldDefinition.name);

  return (
    <Flex flexDirection={'column'} gap={2}>
      {isEditing && onChange && (
        <BooleanUpdateModal
          formField={formFieldDefinition}
          isOpen={isEditing}
          onCancel={() => {
            setIsEditing(false);
          }}
          value={value}
          onSubmit={(value: boolean) => {
            setIsEditing(false);
            onChange(value);
          }}
        />
      )}

      {isUpdated && showDiffModal ? (
        <BooleanDiffModal
          config={formFieldDefinition}
          value={value}
          previousValue={previousValue}
          onClose={closeDiffModal}
        />
      ) : null}

      <FieldHeader
        {...formFieldDefinition}
        label={formFieldDefinition.params.labels.main}
        isUpdated={isUpdated}
        editCallback={editCallback}
        onShowDiffModal={openDiffModal}
        availableActions={availableActions}
        entryPoint={entryPoint}
        isUpdateRequired={isUpdateRequired}
      />

      {typeof value === 'boolean' && value !== undefined ? (
        <BooleanDisplay
          value={value}
          subtitle={
            formFieldDefinition.params.labels.options?.[String(value)]?.main
          }
        />
      ) : (
        <BooleanEmptyState />
      )}
    </Flex>
  );
};
